import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Sparkles, Plus, Trash2, Package, TrendingUp,
  Percent, Zap, Edit, BarChart3
} from "lucide-react";

export default function CombosPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [combos, setCombos] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editCombo, setEditCombo] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", product_names: [], product_ids: [], original_price: 0, combo_price: 0, discount_pct: 10 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    try {
      const [combosRes, suggestionsRes] = await Promise.all([
        authAxiosRef.current.get("/combos"),
        authAxiosRef.current.get("/combos/suggestions"),
      ]);
      setCombos(combosRes.data);
      setSuggestions(suggestionsRes.data);
    } catch { toast.error("Error cargando combos"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateFromSuggestion = (s) => {
    setForm({
      name: `Combo ${s.product_names[0]} + ${s.product_names[1]}`,
      description: s.product_names.join(" + "),
      product_ids: s.product_ids,
      product_names: s.product_names,
      original_price: s.original_price,
      combo_price: s.suggested_combo_price,
      discount_pct: s.suggested_discount_pct,
    });
    setEditCombo(null);
    setShowCreate(true);
  };

  const handleSave = async () => {
    try {
      if (editCombo) {
        await authAxios.patch(`/combos/${editCombo.id}`, {
          name: form.name, description: form.description,
          combo_price: Number(form.combo_price), discount_pct: Number(form.discount_pct),
        });
        toast.success("Combo actualizado");
      } else {
        await authAxios.post("/combos", {
          ...form, combo_price: Number(form.combo_price), discount_pct: Number(form.discount_pct),
          original_price: Number(form.original_price),
        });
        toast.success("Combo creado!");
      }
      setShowCreate(false);
      setEditCombo(null);
      fetchData();
    } catch { toast.error("Error guardando combo"); }
  };

  const handleToggle = async (combo) => {
    try {
      await authAxios.patch(`/combos/${combo.id}`, { active: !combo.active });
      fetchData();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (id) => {
    try {
      await authAxios.delete(`/combos/${id}`);
      toast.success("Combo eliminado");
      fetchData();
    } catch { toast.error("Error eliminando"); }
  };

  const handleEdit = (combo) => {
    setForm({
      name: combo.name, description: combo.description,
      product_ids: combo.product_ids, product_names: combo.product_names || [],
      original_price: combo.original_price || 0, combo_price: combo.combo_price,
      discount_pct: combo.discount_pct || 10,
    });
    setEditCombo(combo);
    setShowCreate(true);
  };

  const sugs = suggestions?.suggestions || [];
  const totalAnalyzed = suggestions?.total_orders_analyzed || 0;

  return (
    <MainLayout>
    <div className="space-y-6" data-testid="combos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />Combo Inteligente
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Combos automaticos basados en pedidos reales</p>
        </div>
        <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => { setForm({ name: "", description: "", product_names: [], product_ids: [], original_price: 0, combo_price: 0, discount_pct: 10 }); setEditCombo(null); setShowCreate(true); }} data-testid="create-combo-btn">
          <Plus className="w-4 h-4" />Crear Combo
        </Button>
      </div>

      {/* AI Suggestions */}
      {sugs.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold flex items-center gap-2 text-amber-800">
              <Zap className="w-4 h-4 text-amber-500" />
              Sugerencias IA ({totalAnalyzed} pedidos analizados)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sugs.map((s, i) => (
              <div key={i} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100" data-testid={`combo-suggestion-${i}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {s.product_names.map((n, j) => (
                      <span key={j}>
                        <Badge className="bg-gray-100 text-gray-700 text-xs">{n}</Badge>
                        {j < s.product_names.length - 1 && <span className="text-gray-400 mx-1">+</span>}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />{s.times_ordered_together}x juntos</span>
                    <span className="flex items-center gap-1"><Percent className="w-3 h-3" />-{s.suggested_discount_pct}%</span>
                    <span className="line-through text-gray-400">{formatPrice(s.original_price)}</span>
                    <span className="font-bold text-emerald-600">{formatPrice(s.suggested_combo_price)}</span>
                  </div>
                </div>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 gap-1 text-xs" onClick={() => handleCreateFromSuggestion(s)} data-testid={`activate-suggestion-${i}`}>
                  <Zap className="w-3 h-3" />Activar
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {sugs.length === 0 && !loading && (
        <Card className="border-gray-200 bg-gray-50/50">
          <CardContent className="py-8 text-center">
            <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Las sugerencias apareceran cuando tengas mas pedidos con productos variados.</p>
            <p className="text-xs text-gray-400 mt-1">{totalAnalyzed} pedidos analizados</p>
          </CardContent>
        </Card>
      )}

      {/* Active Combos */}
      <div>
        <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Combos Activos ({combos.filter(c => c.active).length})</h3>
        {combos.length === 0 && !loading && (
          <p className="text-sm text-gray-400 text-center py-8">No tienes combos creados. Usa las sugerencias o crea uno manualmente.</p>
        )}
        <div className="grid gap-3">
          {combos.map(combo => (
            <Card key={combo.id} className={`${combo.active ? "border-emerald-200" : "border-gray-200 opacity-60"}`} data-testid={`combo-card-${combo.id.slice(-6)}`}>
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-emerald-600" />
                    <span className="font-bold text-gray-900 text-sm">{combo.name}</span>
                    {combo.active ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Activo</Badge> : <Badge className="bg-gray-100 text-gray-500 text-[10px]">Inactivo</Badge>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-6">{combo.description}</p>
                  <div className="flex items-center gap-3 mt-1 ml-6">
                    {combo.original_price > 0 && <span className="text-xs text-gray-400 line-through">{formatPrice(combo.original_price)}</span>}
                    <span className="text-sm font-bold text-emerald-600">{formatPrice(combo.combo_price)}</span>
                    {combo.discount_pct > 0 && <Badge className="bg-red-100 text-red-600 text-[10px]">-{combo.discount_pct}%</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={combo.active} onCheckedChange={() => handleToggle(combo)} data-testid={`toggle-combo-${combo.id.slice(-6)}`} />
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(combo)}><Edit className="w-4 h-4 text-gray-400" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(combo.id)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-bold">{editCombo ? "Editar Combo" : "Crear Combo"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold text-gray-500">NOMBRE</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Combo del Dia" data-testid="combo-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold text-gray-500">DESCRIPCION</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Sushi Roll + Cerveza" data-testid="combo-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-500">PRECIO ORIGINAL</Label>
                <Input type="number" step="0.01" value={form.original_price} onChange={e => setForm({ ...form, original_price: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-bold text-gray-500">PRECIO COMBO</Label>
                <Input type="number" step="0.01" value={form.combo_price} onChange={e => setForm({ ...form, combo_price: e.target.value })} data-testid="combo-price-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold text-gray-500">DESCUENTO %</Label>
              <Input type="number" value={form.discount_pct} onChange={e => setForm({ ...form, discount_pct: e.target.value })} />
            </div>
            {form.product_names.length > 0 && (
              <div>
                <Label className="text-xs font-bold text-gray-500">PRODUCTOS</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {form.product_names.map((n, i) => <Badge key={i} className="bg-emerald-100 text-emerald-700">{n}</Badge>)}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSave} disabled={!form.name || !form.combo_price} data-testid="save-combo-btn">
                {editCombo ? "Guardar" : "Crear Combo"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </MainLayout>
  );
}
